import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Star, RotateCcw, Home, ArrowLeft } from "lucide-react";

/* Screen 7 — AI Review (approved SalesPit coaching dashboard). */

const DEFAULT_REVIEW = {
  overall: 8.6,
  metrics: [
    { label: "Rapport", value: 8.7 },
    { label: "Discovery", value: 7.9 },
    { label: "Objection Handling", value: 8.1 },
    { label: "Closing", value: 9.2 },
  ],
  strengths: [
    "Great rapport building",
    "Good use of open-ended questions",
    "Confident in closing",
  ],
  improvements: [
    "Dig deeper into pain points",
    "Handle objections with more structure",
    "Slow down when explaining solutions",
  ],
  focus: "Focus on discovery. Ask more questions and uncover deeper pain points.",
};

const AUTO_REMATCH_SECONDS = 60;

const colorFor = (v) => (v >= 8 ? "#22C55E" : v >= 7 ? "#F5A524" : "#E52525");

const OverallGauge = ({ score }) => {
  const R = 54;
  const C = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(1, score / 10));
  return (
    <div className="relative flex h-36 w-36 items-center justify-center">
      <svg width="144" height="144" viewBox="0 0 144 144">
        <circle cx="72" cy="72" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="11" />
        <motion.circle
          cx="72"
          cy="72"
          r={R}
          fill="none"
          stroke={colorFor(score)}
          strokeWidth="11"
          strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          animate={{ strokeDashoffset: C * (1 - pct) }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          transform="rotate(-90 72 72)"
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-heading text-4xl font-black leading-none text-white">{score}</span>
        <span className="mt-1 text-xs font-medium text-zinc-500">/10</span>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, delay }) => {
  const color = colorFor(value);
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="flex flex-col rounded-2xl border border-white/10 bg-[#141414] p-5"
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="text-xs font-medium uppercase tracking-wide text-zinc-400">{label}</span>
      <span className="font-heading mt-2 text-3xl font-black leading-none text-white">{value}</span>
      <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${(value / 10) * 100}%` }}
          transition={{ duration: 1, delay: delay + 0.2, ease: "easeOut" }}
        />
      </div>
    </motion.div>
  );
};

const ListCard = ({ title, items, positive, delay, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="rounded-2xl border border-white/10 bg-[#141414] p-6"
    data-testid={testid}
  >
    <h3 className="font-heading text-base font-bold text-white">{title}</h3>
    <ul className="mt-4 space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm text-zinc-300">
          {positive ? (
            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#22C55E]" />
          ) : (
            <AlertCircle size={18} className="mt-0.5 shrink-0 text-[#E52525]" />
          )}
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export default function AIReview() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const review = state?.review || null;
  const historical = !!review;
  const data = review || DEFAULT_REVIEW;

  const [countdown, setCountdown] = useState(AUTO_REMATCH_SECONDS);
  const acted = useRef(false);

  useEffect(() => {
    if (historical) return; // reviewing a past session — no auto-rematch
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(id);
          if (!acted.current) {
            acted.current = true;
            navigate("/matchmaking", { replace: true });
          }
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (path) => {
    acted.current = true;
    navigate(path);
  };

  return (
    <main
      className="relative min-h-screen bg-[#0A0A0A] px-6 py-10 font-body text-white md:px-10"
      data-testid="ai-review-page"
    >
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E52525]">
            AI Coaching
          </span>
          <h1 className="font-heading mt-2 text-3xl font-black tracking-tight text-white sm:text-4xl">
            {historical ? `Session with ${review.name}` : "Your call review"}
          </h1>
          {historical && (
            <p className="mt-2 text-sm text-zinc-400" data-testid="review-session-meta">
              {review.date}
              {review.duration ? ` · ${review.duration}` : ""}
            </p>
          )}
        </motion.div>

        {/* Scores row */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="col-span-2 flex flex-col items-center justify-center rounded-2xl border border-[#E52525]/20 bg-[#141414] p-5 lg:col-span-1"
            data-testid="overall-score"
          >
            <span className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-400">
              Overall Score
            </span>
            <OverallGauge score={data.overall} />
          </motion.div>

          {data.metrics.map((m, i) => (
            <MetricCard key={m.label} label={m.label} value={m.value} delay={0.1 + i * 0.08} />
          ))}
        </div>

        {/* Feedback row */}
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <ListCard title="Strengths" items={data.strengths} positive delay={0.15} testid="strengths-card" />
          <ListCard
            title="Areas to Improve"
            items={data.improvements}
            positive={false}
            delay={0.22}
            testid="improvements-card"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.29 }}
            className="rounded-2xl border border-[#E52525]/20 bg-gradient-to-b from-[#1a1414] to-[#141414] p-6"
            data-testid="todays-focus-card"
          >
            <div className="flex items-center gap-2">
              <Star size={18} className="text-[#E52525]" />
              <h3 className="font-heading text-base font-bold text-white">Today's Focus</h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-zinc-300">{data.focus}</p>
          </motion.div>
        </div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.36 }}
          className="mt-8 flex flex-col gap-3 sm:flex-row"
        >
          <button
            data-testid="match-again-btn"
            onClick={() => go("/matchmaking")}
            className="group inline-flex flex-1 items-center justify-center gap-2.5 rounded-full bg-[#E52525] px-8 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.3)] transition-[background-color,transform] duration-300 hover:bg-[#FF3333] active:scale-[0.98]"
          >
            <RotateCcw size={19} />
            {historical ? "Match Again" : `Match Again (${countdown}s)`}
          </button>
          <button
            data-testid="back-home-btn"
            onClick={() => go(historical ? "/history" : "/homepage")}
            className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/15 bg-transparent px-8 py-4 text-base font-semibold text-white transition-colors duration-300 hover:bg-white/5 sm:flex-none"
          >
            {historical ? <ArrowLeft size={18} /> : <Home size={18} />}
            {historical ? "Back to History" : "Back to Home"}
          </button>
        </motion.div>
      </div>
    </main>
  );
}
